import React, { useState, useEffect, useMemo } from 'react';
import { View } from '../../types';
import { useAuth } from '../../context/AuthContext';
import {
    Activity, Heart, Search, Plus, QrCode,
    ShieldCheck, Settings, Users, Home,
    ArrowRight, CheckCircle, Info, Filter,
    Camera, Trash2, Save, X, FileText,
    MessageSquare, Award, Clock, Loader2,
    Calendar, MapPin, Phone, Mail, ChevronRight,
    User as UserIcon, Map as MapIcon, Mars, Venus,
    Scale, Ruler, ExternalLink, Shield,
    Stethoscope, Files, Upload, UserPlus as UserPlusIcon,
    Building2, Globe, Dog, Syringe
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '../../services/api';
import { LOCATIONS } from '../../data/locations';
import { formatGenericID } from '../../utils/formatters';
import { SPECIES_GROUPS, SPECIES_CONFIG } from '../../data/species';
import AddPetForm from '../../components/AddPetForm';
import { ShelterSponsors } from './ShelterSponsors';
import { BreederPetHealth } from '../Breeder/BreederPetHealth';

// Leaflet
// import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
// import L from 'leaflet';
// import 'leaflet/dist/leaflet.css';

// Fix Leaflet icons
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Status labels for rescue pets
const statusLabels = {
    'disponible': { label: 'En Adopción', color: 'bg-emerald-100 text-emerald-600' },
    'reservado': { label: 'En Trámite', color: 'bg-amber-100 text-amber-600' },
    'emergencia': { label: 'Caso Crítico', color: 'bg-rose-100 text-rose-600' },
    'recuperacion': { label: 'Recuperación', color: 'bg-indigo-100 text-indigo-600' },
    'adoptado': { label: 'Adoptado', color: 'bg-slate-100 text-slate-500' }
};

interface ShelterDashboardProps {
    onExit: (view: View) => void;
    monitoringUser?: any;
}

const LocationMarker = ({ position, setPosition }: { position: [number, number], setPosition: (pos: [number, number]) => void }) => {
    const map = useMapEvents({
        click(e) {
            setPosition([e.latlng.lat, e.latlng.lng]);
        },
    });

    return position ? <Marker position={position} /> : null;
};

const ChangeMapCenter = ({ center }: { center: [number, number] }) => {
    const map = useMap();
    useEffect(() => {
        map.setView(center);
    }, [center, map]);
    return null;
};

export const ShelterDashboard: React.FC<ShelterDashboardProps> = ({ onExit, monitoringUser }) => {
    const { user: authUser } = useAuth();
    const user = monitoringUser || authUser;
    const [activeTab, setActiveTab] = useState<'dashboard' | 'residentes' | 'pet_detail' | 'adopciones' | 'padrinos' | 'settings'>('dashboard');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [isDemoMode, setIsDemoMode] = useState(false);

    // Data states
    const [residents, setResidents] = useState<any[]>([]);
    const [adoptions, setAdoptions] = useState<any[]>([]);
    const [sponsors, setSponsors] = useState<any[]>([]);
    const [shelterProfile, setShelterProfile] = useState<any>({
        name: '',
        location: '',
        capacity: 50,
        mission: '',
        phone: '',
        email: '',
        verified: 0,
        manager_name: '',
        manager_rut: '',
        manager_phone: '',
        country: 'Chile',
        region: '',
        comuna: '',
        city: '',
        address: '',
        lat: -33.4489,
        lng: -70.6693
    });

    const [stats, setStats] = useState({
        total: 0,
        available: 0,
        critical: 0,
        adoptions: 0
    });

    // Modals
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [selectedPetDetail, setSelectedPetDetail] = useState<any>(null);
    const [activePetTab, setActivePetTab] = useState<'protocolo' | 'crecimiento' | 'carnet' | 'tratamientos' | 'historial' | 'documentos'>('protocolo');
    const [selectedPetHealth, setSelectedPetHealth] = useState<{
        vaccines: any[],
        consultations: any[],
        medications: any[],
        growth: any[],
        gallery: any[]
    }>({ vaccines: [], consultations: [], medications: [], growth: [], gallery: [] });

    // Growth Form
    const [growthForm, setGrowthForm] = useState({
        weight: '',
        height: ''
    });

    const [petDocs, setPetDocs] = useState<any[]>([]);
    const [showAddDocModal, setShowAddDocModal] = useState(false);
    const [docFormData, setDocFormData] = useState({ title: '', type: 'Examen de Salud', file: null as File | null });

    const [showVaccineModal, setShowVaccineModal] = useState(false);
    const [showConsultationModal, setShowConsultationModal] = useState(false);
    const [healthFormData, setHealthFormData] = useState<any>({});
    const [tempPrescriptions, setTempPrescriptions] = useState<any[]>([]);
    const [prescriptionForm, setPrescriptionForm] = useState({ name: '', start: '09:00', freq: '8', duration: '5' });
    const [consultationErrors, setConsultationErrors] = useState<Record<string, boolean>>({});
    const [successModal, setSuccessModal] = useState<{ isOpen: boolean, title: string, message: string }>({ isOpen: false, title: '', message: '' });

    // Adoption / Vincular modal
    const [showAdoptionModal, setShowAdoptionModal] = useState(false);
    const [selectedAdoptionPet, setSelectedAdoptionPet] = useState<any>(null);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [isSearchingUsers, setIsSearchingUsers] = useState(false);
    const [foundUsers, setFoundUsers] = useState<any[]>([]);

    const countries = Object.keys(LOCATIONS);
    const regions = shelterProfile.country ? Object.keys(LOCATIONS[shelterProfile.country]?.regions || {}) : [];
    const communes = (shelterProfile.country && shelterProfile.region) ? LOCATIONS[shelterProfile.country].regions[shelterProfile.region] : [];

    // Protocol System
    const defaultProtocolOptions = [
        { id: 'q', label: '14 días de Cuarentena Cumplida', manual: true },
        { id: 't', label: 'Test de Virus / Enfermedades Infecciosas', manual: true },
        { id: 'd', label: 'Desparasitación Interna y Externa', manual: true },
        { id: 'v', label: 'Vacunas Base Inyectadas', manual: true },
        { id: 's', label: 'Esterilización / Castración', manual: true },
        { id: 'a', label: 'Aprobado para Adopción (Alta Médica)', manual: true }
    ];
    const [protocolOptions, setProtocolOptions] = useState<any[]>(defaultProtocolOptions);

    // Demo Data Seeder
    const loadDemoData = () => {
        const demoResidents = [
            { id: 101, name: 'Pirata', species: 'Perro', breed: 'Galgo', status: 'emergencia', image_url: 'https://images.unsplash.com/photo-1518717758536-85ae29035b6d', sterilized: 0, gender: 'Macho', birthDate: '2020-05-12', created_at: new Date().toISOString() },
            { id: 102, name: 'Luna', species: 'Gato', breed: 'Siamés', status: 'disponible', image_url: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba', sterilized: 1, gender: 'Hembra', birthDate: '2022-01-10', created_at: new Date().toISOString() },
            { id: 103, name: 'Rambo', species: 'Perro', breed: 'Pastor Alemán', status: 'recuperacion', image_url: 'https://images.unsplash.com/photo-1589941013453-ec89f33b5e95', sterilized: 1, gender: 'Macho', birthDate: '2019-11-20', created_at: new Date().toISOString() },
            { id: 104, name: 'Canela', species: 'Perro', breed: 'Cocker', status: 'disponible', image_url: 'https://images.unsplash.com/photo-1537151608828-ea2b11777ee8', sterilized: 1, gender: 'Hembra', birthDate: '2021-08-15', created_at: new Date().toISOString() },
            { id: 105, name: 'Bigotes', species: 'Gato', breed: 'Mestizo', status: 'emergencia', image_url: 'https://images.unsplash.com/photo-1573865526739-10659fec78a5', sterilized: 0, gender: 'Macho', birthDate: '2023-02-22', created_at: new Date().toISOString() }
        ];

        const demoAdoptions = [
            { id: 1, pet_name: 'Luna', pet_image: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba', applicant_name: 'Juan Pérez', applicant_email: 'juan@email.com', applicant_phone: '+569 1234 5678', home_type: 'departamento', status: 'pending' },
            { id: 2, pet_name: 'Canela', pet_image: 'https://images.unsplash.com/photo-1537151608828-ea2b11777ee8', applicant_name: 'María García', applicant_email: 'maria@email.com', applicant_phone: '+569 8765 4321', home_type: 'casa', status: 'pending' }
        ];

        const demoSponsors = [
            { id: 1, user_name: 'Carlos Arriaga', pet_name: 'Pirata', monthly_amount: 15000 },
            { id: 2, user_name: 'Sofía Valdés', pet_name: 'Rambo', monthly_amount: 25000 }
        ];

        setResidents(demoResidents);
        setAdoptions(demoAdoptions);
        setSponsors(demoSponsors);
        setIsDemoMode(true);
        setStats({
            total: 5,
            available: 2,
            critical: 2,
            adoptions: 0
        });
        alert('Datos de demostración cargados en todo el entorno.');
    };

    // Fetch data based on tab
    useEffect(() => {
        if (!user) return;
        fetchAllData();
    }, [user, activeTab]);

    const fetchAllData = async () => {
        if (isDemoMode) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const profileRes = await api.shelter.getProfile(user?.id || '');
            if (profileRes.success && profileRes.shelter) {
                setShelterProfile({
                    ...profileRes.shelter,
                    lat: parseFloat(profileRes.shelter.lat) || -33.4489,
                    lng: parseFloat(profileRes.shelter.lng) || -70.6693
                });
                if (profileRes.shelter.protocol_options) {
                    setProtocolOptions(profileRes.shelter.protocol_options);
                }
            }

            if (activeTab === 'dashboard' || activeTab === 'residentes') {
                // Determine the best ID to use for fetching residents
                const targetShelterId = profileRes.shelter?.id;
                console.log('Fetching residents for shelter:', targetShelterId || user?.id);

                const resRes = await api.shelter.getResidents(user?.id, targetShelterId);
                if (resRes.success) {
                    const residentList = resRes.residents || [];
                    setResidents(residentList);
                    setStats({
                        total: residentList.length,
                        available: residentList.filter((r: any) => r.status === 'disponible').length,
                        critical: residentList.filter((r: any) => r.status === 'emergencia').length,
                        adoptions: residentList.filter((r: any) => r.status === 'adoptado').length
                    });
                }
            }

            if (activeTab === 'adopciones') {
                const adoptRes = await api.shelter.getAdoptions(user?.id);
                if (adoptRes.success) setAdoptions(adoptRes.requests);
            }

            // Padrinos: ShelterSponsors carga sus propios datos desde sponsors.php.
        } catch (error) {
            console.error("Error fetching shelter data", error);
        } finally {
            setLoading(false);
        }
    };

    const loadPetDocs = async (petId: any) => {
        try {
            const data = await api.getPetDocuments(petId);
            setPetDocs(data || []);
        } catch (e) {
            console.error("Error loading pet docs:", e);
        }
    };

    const fetchPetHealthData = async (petId: string | number) => {
        try {
            const [vacs, meds, health, growth, gallery] = await Promise.all([
                api.getVaccines(String(petId)),
                api.getMedications(String(petId)),
                api.getHealthRecords(String(petId)),
                api.getGrowthRecords(String(petId)),
                api.getPetGallery(String(petId))
            ]);
            loadPetDocs(petId);
            setSelectedPetHealth({
                vaccines: Array.isArray(vacs) ? vacs : [],
                medications: Array.isArray(meds) ? meds : [],
                consultations: Array.isArray(health) ? health : [],
                growth: Array.isArray(growth) ? growth : [],
                gallery: Array.isArray(gallery) ? gallery : []
            });
        } catch (error) {
            console.error("Error fetching pet health", error);
        }
    };

    useEffect(() => {
        if (selectedPetDetail) {
            fetchPetHealthData(selectedPetDetail.id);
        }
    }, [selectedPetDetail]);

    const handleUploadDocument = async () => {
        if (!docFormData.title || !docFormData.file || !selectedPetDetail) return alert("Completa todos los campos");
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', docFormData.file);
            formData.append('type', 'document');

            const uploadRes = await fetch(`${api.API_URL}/upload.php`, {
                method: 'POST',
                body: formData
            });
            const text = await uploadRes.text();
            let uploadResult;
            try {
                uploadResult = JSON.parse(text);
            } catch (e) {
                throw new Error("Respuesta inválida del servidor al subir");
            }

            if (!uploadResult.success) throw new Error(uploadResult.error || "Error al subir archivo");

            await api.addPetDocument({
                petId: selectedPetDetail.id,
                title: docFormData.title,
                document_type: docFormData.type,
                file_url: uploadResult.url
            });

            await loadPetDocs(selectedPetDetail.id);
            setShowAddDocModal(false);
            setDocFormData({ title: '', type: 'Examen de Salud', file: null as File | null });

            setSuccessModal({
                isOpen: true,
                title: 'Documento Guardado',
                message: 'El documento ha sido anexado exitosamente.'
            });
        } catch (e: any) {
            console.error(e);
            alert(`Error al subir documento: ${e.message || 'Error desconocido'}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddVaccine = async () => {
        if (!healthFormData.name || !healthFormData.date || !selectedPetDetail) return alert("Completa los campos obligatorios");
        setActionLoading(true);
        try {
            await api.addVaccine({
                pet_id: selectedPetDetail.id,
                name: healthFormData.name,
                date_applied: healthFormData.date,
                place: healthFormData.place || 'Veterinaria'
            });
            setSuccessModal({
                isOpen: true,
                title: 'Vacuna Registrada',
                message: 'La información se ha guardado en el carnet sanitario.'
            });
            setShowVaccineModal(false);
            setHealthFormData({});
            fetchPetHealthData(selectedPetDetail.id);
        } catch (e) {
            console.error(e);
            alert("Error al registrar vacuna");
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddConsultation = async () => {
        const errors: Record<string, boolean> = {};
        if (!healthFormData.type) errors.type = true;
        if (!healthFormData.date) errors.date = true;
        if (!healthFormData.diagnosis) errors.diagnosis = true;
        if (!healthFormData.place) errors.place = true;

        if (Object.keys(errors).length > 0) {
            setConsultationErrors(errors);
            return;
        }

        setActionLoading(true);
        setConsultationErrors({});
        try {
            await api.addHealthRecord({
                petId: selectedPetDetail.id,
                date: healthFormData.date,
                type: healthFormData.type,
                diagnosis: healthFormData.diagnosis,
                recommendation: healthFormData.recommendation,
                doctor: healthFormData.doctor,
                clinic: healthFormData.place || 'Control',
                prescriptions: tempPrescriptions
            });

            setSuccessModal({
                isOpen: true,
                title: 'Consulta Guardada',
                message: 'El historial médico ha sido actualizado.'
            });
            setShowConsultationModal(false);
            setHealthFormData({});
            setTempPrescriptions([]);
            fetchPetHealthData(selectedPetDetail.id);
        } catch (e: any) {
            console.error(e);
            alert(`Error al registrar consulta: ${e.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleAddPrescription = () => {
        if (!prescriptionForm.name) return;
        const newP = {
            id: Date.now(),
            ...prescriptionForm
        };
        setTempPrescriptions([...tempPrescriptions, newP]);
        setPrescriptionForm({
            name: '',
            start: '09:00',
            freq: '8',
            duration: '5'
        });
    };

    const handleRemovePrescription = (id: number) => {
        setTempPrescriptions(tempPrescriptions.filter(p => p.id !== id));
    };

    const handleSaveGrowth = async () => {
        if (!selectedPetDetail || !growthForm.weight) return;
        setActionLoading(true);
        try {
            await api.addGrowthRecord({
                petId: String(selectedPetDetail.id),
                date: new Date().toISOString().split('T')[0],
                weight: parseFloat(growthForm.weight),
                height: parseFloat(growthForm.height || '0'),
                note: 'Seguimiento de Refugio'
            });
            fetchPetHealthData(selectedPetDetail.id);
            setGrowthForm({ weight: '', height: '' });
        } catch (e: any) {
            alert('Error al guardar registro: ' + (e.message || String(e)));
            console.error(e);
        } finally {
            setActionLoading(false);
        }
    };

    const handleUploadGalleryImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !selectedPetDetail) return;
        const file = e.target.files[0];
        setActionLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('type', 'gallery');
            const res = await fetch(`${api.API_URL}/upload.php`, { method: 'POST', body: formData });
            const data = await res.json();
            if (data.success) {
                await api.addPetGalleryImage({ petId: String(selectedPetDetail.id), url: data.url });
                fetchPetHealthData(selectedPetDetail.id);
            } else {
                alert('Error al subir imagen: ' + data.error);
            }
        } catch (error: any) {
            console.error(error);
            alert('Error de red: ' + error.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateProfilePicture = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0] || !selectedPetDetail) return;
        const file = e.target.files[0];
        setActionLoading(true);
        try {
            const res = await api.uploadImage(file);
            if (res.success) {
                await api.updatePet(selectedPetDetail.id, { imageUrl: res.url });
                // Update local state
                setSelectedPetDetail({ ...selectedPetDetail, imageUrl: res.url });
                // Update residents list
                setResidents(prev => prev.map(p => p.id === selectedPetDetail.id ? { ...p, imageUrl: res.url } : p));
            } else {
                alert('Error al subir imagen: ' + res.message);
            }
        } catch (error: any) {
            console.error(error);
            alert('Error de red: ' + error.message);
        } finally {
            setActionLoading(false);
        }
    };


    const handleDeleteGalleryImage = async (imageId: string | number) => {
        if (!confirm('¿Seguro de eliminar esta imagen?')) return;
        setActionLoading(true);
        try {
            await api.deletePetGalleryImage(imageId);
            if (selectedPetDetail) fetchPetHealthData(selectedPetDetail.id);
        } catch (error) {
            console.error(error);
            alert('Error al eliminar imagen');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateProfile = async () => {
        setActionLoading(true);
        try {
            const res = await api.shelter.updateProfile({
                userId: user?.id,
                ...shelterProfile,
                protocol_options: protocolOptions
            });
            if (res.success) alert('Perfil actualizado con éxito');
            else alert('Error: ' + res.error);
        } catch (e) {
            alert('Error de conexión');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSearchUsers = async (q: string) => {
        setUserSearchTerm(q);
        if (q.length < 3) {
            setFoundUsers([]);
            return;
        }
        setIsSearchingUsers(true);
        try {
            const res = await api.searchUsers(q);
            setFoundUsers(res.users || []);
        } catch (e) {
            console.error(e);
        } finally {
            setIsSearchingUsers(false);
        }
    };

    const handleStartAdoption = async (pet: any, targetUser: any) => {
        setActionLoading(true);
        try {
            // Enviar correo de adopción
            await fetch(`${api.API_URL}/email_templates_api.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'send',
                    type: 'adoption',
                    email_to: targetUser.email,
                    data: {
                        pet_name: pet.name,
                        center_name: shelterProfile?.name || 'Nuestro Albergue'
                    }
                })
            });

            setSuccessModal({
                isOpen: true,
                title: 'Vinculación de Rescate Iniciada',
                message: `Se ha enviado un correo a ${targetUser.email} con los pasos para confirmar la adopción.`
            });
            setShowAdoptionModal(false);
            setFoundUsers([]);
            setUserSearchTerm('');
        } catch (error) {
            console.error(error);
            alert("Error al vincular dueño.");
        } finally {
            setActionLoading(false);
        }
    };

    const handleRegisterRescue = async () => {
        if (!newPetData.name) return;
        setActionLoading(true);
        try {
            const res = await api.addPet({
                ...newPetData,
                userId: user?.id,
                shelter_id: shelterProfile.id,
                rescue_status: 'shelter'
            });
            if (res.success) {
                setShowRegisterModal(false);
                fetchAllData();
            }
        } catch (e) {
            alert('Error al registrar');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateAdoptionStatus = async (requestId: number, status: string) => {
        if (!confirm(`¿Cambiar estado a ${status}?`)) return;
        try {
            const res = await api.shelter.updateAdoptionStatus(requestId, status);
            if (res.success) fetchAllData();
        } catch (e) {
            alert('Error al actualizar');
        }
    };

    const renderDashboard = () => (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Welcome / Empty State Banner */}
            {residents.length === 0 && (
                <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-[3rem] p-12 text-white shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl group-hover:scale-110 transition-transform duration-1000"></div>
                    <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                        <div>
                            <h2 className="text-4xl font-black uppercase italic tracking-tighter mb-4">¡Bienvenido a ShelterPro!</h2>
                            <p className="text-indigo-100 font-medium max-w-xl leading-relaxed">
                                Parece que aún no tienes mascotas registradas. ¿Quieres ver cómo funciona todo el ecosistema?
                                Carga datos de muestra para explorar las bitácoras médicas, solicitudes de adopción y más.
                            </p>
                        </div>
                        <button
                            onClick={loadDemoData}
                            className="bg-white text-indigo-600 px-10 py-6 rounded-[2rem] font-black uppercase text-xs tracking-widest shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center gap-3 shrink-0"
                        >
                            <Activity size={20} /> Cargar Entorno Demo
                        </button>
                    </div>
                </div>
            )}

            {/* Hero Stats */}
            <div className="grid grid-cols-4 gap-2 lg:gap-6 w-full">
                <div className="bg-white p-3 lg:p-8 rounded-2xl lg:rounded-[2.5rem] shadow-xl border border-slate-100 flex flex-col items-center text-center group hover:scale-105 transition-transform">
                    <div className="p-2 lg:p-4 bg-orange-50 text-orange-500 rounded-xl lg:rounded-2xl mb-2 lg:mb-4 group-hover:rotate-12 transition-transform">
                        <Home size={20} className="lg:w-7 lg:h-7" />
                    </div>
                    <p className="text-xl lg:text-4xl font-black text-slate-800 italic leading-none">{stats.total}</p>
                    <p className="text-[8px] lg:text-[10px] font-black text-slate-400 uppercase tracking-tight lg:tracking-widest mt-1 truncate w-full">Residentes</p>
                </div>

                <div className="bg-white p-3 lg:p-8 rounded-2xl lg:rounded-[2.5rem] shadow-xl border border-slate-100 flex flex-col items-center text-center group hover:scale-105 transition-transform">
                    <div className="p-2 lg:p-4 bg-emerald-50 text-emerald-500 rounded-xl lg:rounded-2xl mb-2 lg:mb-4 group-hover:rotate-12 transition-transform">
                        <Heart size={20} className="lg:w-7 lg:h-7" />
                    </div>
                    <p className="text-xl lg:text-4xl font-black text-slate-800 italic leading-none">{stats.available}</p>
                    <p className="text-[8px] lg:text-[10px] font-black text-slate-400 uppercase tracking-tight lg:tracking-widest mt-1 truncate w-full">Adopción</p>
                </div>

                <div className="bg-white p-3 lg:p-8 rounded-2xl lg:rounded-[2.5rem] shadow-xl border border-slate-100 flex flex-col items-center text-center group hover:scale-105 transition-transform">
                    <div className="p-2 lg:p-4 bg-rose-50 text-rose-500 rounded-xl lg:rounded-2xl mb-2 lg:mb-4 group-hover:rotate-12 transition-transform">
                        <Activity size={20} className="lg:w-7 lg:h-7" />
                    </div>
                    <p className="text-xl lg:text-4xl font-black text-slate-800 italic leading-none">{stats.critical}</p>
                    <p className="text-[8px] lg:text-[10px] font-black text-slate-400 uppercase tracking-tight lg:tracking-widest mt-1 truncate w-full">Críticos</p>
                </div>

                <div className="bg-indigo-600 p-3 lg:p-8 rounded-2xl lg:rounded-[2.5rem] shadow-xl flex flex-col items-center text-center group hover:scale-105 transition-transform text-white">
                    <div className="p-2 lg:p-4 bg-white/20 text-white rounded-xl lg:rounded-2xl mb-2 lg:mb-4 group-hover:rotate-12 transition-transform">
                        <Award size={20} className="lg:w-7 lg:h-7" />
                    </div>
                    <p className="text-xl lg:text-4xl font-black italic leading-none">{stats.adoptions}</p>
                    <p className="text-[8px] lg:text-[10px] font-black opacity-80 uppercase tracking-tight lg:tracking-widest mt-1 truncate w-full">Adoptados</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white rounded-[3rem] p-10 shadow-2xl border border-slate-100">
                    <div className="flex justify-between items-center mb-10">
                        <div>
                            <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter">Atención Requerida</h3>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Mascotas que necesitan tu ayuda hoy</p>
                        </div>
                        <div className="flex gap-4">
                            <button
                                onClick={loadDemoData}
                                className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl font-black uppercase text-[9px] tracking-widest hover:bg-indigo-100 transition-all flex items-center gap-2 border border-indigo-100"
                            >
                                <Plus size={14} /> Cargar Datos Demo
                            </button>
                            <button onClick={() => { setActiveTab('residentes'); setIsSidebarOpen(false); }} className="text-[#3F51B5] font-black uppercase text-[10px] tracking-widest hover:underline">Ver Todo</button>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {residents.filter(r => r.status === 'emergencia' || r.status === 'recuperacion').slice(0, 3).map(pet => (
                            <div key={pet.id} onClick={() => { setSelectedPetDetail(pet); setActiveTab('pet_detail'); setIsSidebarOpen(false); }} className="p-5 bg-slate-50 rounded-[2rem] border border-slate-100 flex items-center gap-6 group hover:bg-white hover:shadow-xl transition-all cursor-pointer">
                                <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-md">
                                    <img src={pet.image_url || 'https://images.unsplash.com/photo-1543466835-00a7907e9de1'} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="font-black text-slate-800 uppercase italic">{pet.name}</h4>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{pet.breed} • {statusLabels[pet.status as keyof typeof statusLabels]?.label}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button className="p-3 bg-white rounded-xl text-slate-400 hover:text-[#3F51B5] shadow-sm transition-colors"><MessageSquare size={18} /></button>
                                    <button className="p-3 bg-white rounded-xl text-slate-400 hover:text-rose-500 shadow-sm transition-colors"><Activity size={18} /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-slate-900 rounded-[3rem] p-10 text-white shadow-2xl relative overflow-hidden">
                        <div className="absolute -top-10 -right-10 w-40 h-40 bg-indigo-500 rounded-full blur-[80px] opacity-20"></div>
                        <h3 className="text-xl font-black uppercase italic mb-6">Acciones Rápidas</h3>
                        <div className="space-y-3">
                            <button onClick={() => setShowRegisterModal(true)} className="w-full p-4 bg-white/10 rounded-2xl flex items-center gap-4 hover:bg-white/20 transition-all text-left">
                                <div className="p-2 bg-[#FF6B00] rounded-xl"><Plus size={18} /></div>
                                <span className="font-black uppercase text-[10px] tracking-widest">Registrar Rescate</span>
                            </button>
                            <button onClick={() => { setActiveTab('adopciones'); setIsSidebarOpen(false); }} className="w-full p-4 bg-white/10 rounded-2xl flex items-center gap-4 hover:bg-white/20 transition-all text-left">
                                <div className="p-2 bg-emerald-500 rounded-xl"><Heart size={18} /></div>
                                <span className="font-black uppercase text-[10px] tracking-widest">Ver Adopciones</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderResidents = () => (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-in slide-in-from-bottom-4 duration-500">
            {residents.map(pet => (
                <div key={pet.id} onClick={() => { setSelectedPetDetail(pet); setActiveTab('pet_detail'); setIsSidebarOpen(false); }} className="bg-white rounded-[2.5rem] overflow-hidden shadow-xl border border-slate-100 group hover:shadow-2xl transition-all cursor-pointer">
                    <div className="h-48 relative overflow-hidden">
                        <img src={pet.image_url || 'https://images.unsplash.com/photo-1543466835-00a7907e9de1'} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                        <div className="absolute bottom-4 left-4">
                            <span className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg ${statusLabels[pet.status as keyof typeof statusLabels]?.color || 'bg-slate-100'}`}>
                                {statusLabels[pet.status as keyof typeof statusLabels]?.label || pet.status}
                            </span>
                        </div>
                    </div>
                    <div className="p-8">
                        <h4 className="text-xl font-black text-slate-800 uppercase italic mb-1">{pet.name}</h4>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{pet.breed} • {pet.species}</p>
                    </div>
                </div>
            ))}
            <button onClick={() => setShowRegisterModal(true)} className="bg-slate-50 border-4 border-dashed border-slate-200 rounded-[2.5rem] flex flex-col items-center justify-center p-12 text-slate-400 hover:bg-white hover:border-orange-500 hover:text-orange-500 transition-all min-h-[300px]">
                <Plus size={48} className="mb-4" />
                <span className="font-black uppercase italic tracking-tighter text-xl">Registrar Rescate</span>
            </button>
        </div>
    );

    const renderProtocolContent = (pet: any) => {
        const protocolState = pet.protocol_state || {};

        // Automatic logic checks
        const autoTasks = [
            { id: 'age', label: 'Edad Estimada Registrada', complete: !!pet.birthDate },
            { id: 'weight', label: 'Pesaje Inicial Registrado', complete: (selectedPetHealth?.growth || []).length > 0 },
            { id: 'gallery', label: 'Fotografías de Rescate', complete: (selectedPetHealth?.gallery || []).length > 0 },
            { id: 'chip', label: 'Escaneo de Microchip', complete: !!(pet.chip_number || pet.microchip) },
            { id: 'docs', label: 'Perfil Conductual / Documentación', complete: (petDocs || []).length > 0 }
        ];

        const manualOptions = protocolOptions.filter(o => o.manual);
        const allTasks = [...autoTasks, ...manualOptions.map(o => ({ ...o, id: o.id, complete: !!protocolState[o.id] }))];

        const completedCount = allTasks.filter(t => t.complete).length;
        const totalCount = allTasks.length;
        const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

        const toggleManualTask = async (taskId: string) => {
            const newState = { ...protocolState, [taskId]: !protocolState[taskId] };
            setActionLoading(true);
            try {
                await api.updatePet(pet.id, { protocol_state: newState });
                const updatedPet = { ...pet, protocol_state: newState };
                setSelectedPetDetail(updatedPet);
                setResidents(residents.map(r => r.id === pet.id ? updatedPet : r));
            } catch (e) {
                alert('Error al actualizar protocolo');
            } finally {
                setActionLoading(false);
            }
        };

        return (
            <div className="space-y-10 animate-in fade-in slide-in-from-bottom-5 duration-700">
                {/* Progress Header */}
                <div className="bg-slate-50 rounded-[2.5rem] p-10 border border-slate-100 flex flex-col md:flex-row items-center gap-10">
                    <div className="relative w-32 h-32 flex-shrink-0">
                        <svg className="w-full h-full transform -rotate-90">
                            <circle cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-slate-200" />
                            <circle cx="64" cy="64" r="58" stroke="currentColor" strokeWidth="12" fill="transparent" strokeDasharray={364} strokeDashoffset={364 - (364 * progress) / 100} strokeLinecap="round" className="text-orange-500 transition-all duration-1000 ease-out" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-3xl font-black text-slate-800 italic leading-none">{progress}%</span>
                            <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest mt-1">Avance</span>
                        </div>
                    </div>
                    <div className="flex-1">
                        <h4 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter">Protocolo de Reinserción</h4>
                        <p className="text-slate-500 font-medium text-sm mt-2 leading-relaxed">
                            Completa los pasos necesarios para asegurar que {pet.name} esté listo para encontrar un nuevo hogar.
                            Algunas tareas se marcan automáticamente al completar registros médicos.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Manual Tasks */}
                    <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="p-2 bg-indigo-50 text-indigo-500 rounded-xl"><CheckCircle size={20} /></div>
                            <h5 className="font-black text-slate-800 uppercase italic text-sm tracking-widest">Procedimientos Manuales</h5>
                        </div>
                        <div className="space-y-4">
                            {manualOptions.map(task => (
                                <div key={task.id}
                                    onClick={() => toggleManualTask(task.id)}
                                    className={`p-5 rounded-2xl flex items-center justify-between gap-4 cursor-pointer transition-all border-2 ${protocolState[task.id] ? 'bg-emerald-50/50 border-emerald-500/20 text-emerald-800' : 'bg-slate-50 border-transparent hover:border-slate-200 text-slate-600'}`}>
                                    <div className="flex items-center gap-4">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${protocolState[task.id] ? 'bg-emerald-500 text-white rotate-[360deg]' : 'bg-slate-200 text-slate-400'}`}>
                                            <CheckCircle size={16} />
                                        </div>
                                        <span className="text-xs font-black uppercase tracking-tight">{task.label}</span>
                                    </div>
                                    {protocolState[task.id] && <span className="text-[9px] font-black uppercase tracking-widest text-emerald-500">Completado</span>}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Automatic Tasks */}
                    <div className="bg-white rounded-[2rem] p-8 border border-slate-100 shadow-sm opacity-80">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="p-2 bg-orange-50 text-orange-500 rounded-xl"><Activity size={20} /></div>
                            <h5 className="font-black text-slate-800 uppercase italic text-sm tracking-widest">Hitos Automáticos</h5>
                        </div>
                        <div className="space-y-4">
                            {autoTasks.map(task => (
                                <div key={task.id} className={`p-5 rounded-2xl flex items-center justify-between gap-4 transition-all ${task.complete ? 'bg-indigo-50/50 text-indigo-800' : 'bg-slate-50 text-slate-400'}`}>
                                    <div className="flex items-center gap-4">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${task.complete ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                                            {task.complete ? <CheckCircle size={16} /> : <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
                                        </div>
                                        <span className="text-xs font-black uppercase tracking-tight">{task.label}</span>
                                    </div>
                                    <Shield size={14} className={task.complete ? 'text-indigo-300' : 'text-slate-200'} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderPetDetail = () => {
        if (!selectedPetDetail) return null;
        const pet = selectedPetDetail;

        return (
            <div className="animate-in fade-in duration-500 w-full h-full">
                <div className="bg-[#f8fafc] w-full min-h-[80vh] rounded-[3.5rem] shadow-xl overflow-hidden flex flex-col md:flex-row border border-slate-100 relative">
                    {/* Close Button */}
                    <button
                        onClick={() => { setSelectedPetDetail(null); setActiveTab('residentes'); setIsSidebarOpen(false); }}
                        className="absolute top-8 right-8 z-[120] p-4 bg-white/80 backdrop-blur-md text-slate-400 rounded-full hover:bg-white hover:text-slate-900 shadow-xl transition-all border border-slate-100 group"
                    >
                        <X size={24} className="group-hover:rotate-90 transition-transform duration-300" />
                    </button>

                    {/* SIDEPANEL: Pet Identity */}
                    {/* SIDEPANEL: Pet Identity */}
                    <div className="w-full md:w-[400px] bg-slate-50 p-8 border-r border-slate-100 flex flex-col items-center overflow-y-auto custom-scrollbar h-full">
                        <div className="relative group w-48 h-48 rounded-[3rem] border-8 border-white shadow-xl overflow-hidden mb-6">
                            <img src={pet.image_url || pet.imageUrl || "https://images.unsplash.com/photo-1543466835-00a7907e9de1"} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <label className="cursor-pointer p-4 bg-white/20 backdrop-blur-md rounded-2xl text-white hover:bg-white/40 transition-all">
                                    <Camera size={24} />
                                    <input type="file" accept="image/*" className="hidden" onChange={handleUpdateProfilePicture} />
                                </label>
                            </div>
                        </div>
                        <h3 className="text-2xl font-black text-slate-800 uppercase italic mb-1">{pet.name}</h3>
                        <div className="flex items-center gap-2 mb-6">
                            <span className={`px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${statusLabels[pet.status as keyof typeof statusLabels]?.color || 'bg-white text-slate-800'}`}>
                                {statusLabels[pet.status as keyof typeof statusLabels]?.label || pet.status}
                            </span>
                            <button
                                className="w-7 h-7 bg-slate-900 text-white rounded-full flex items-center justify-center hover:bg-black transition-colors shadow-lg hover:scale-110 transform duration-200"
                                title="Ver Código QR"
                            >
                                <QrCode size={14} />
                            </button>
                        </div>

                        <div className="w-full space-y-4">
                            {pet.pedigreeReg ? (
                                <div className="bg-emerald-50 p-4 rounded-2xl border-2 border-emerald-200 shadow-lg shadow-emerald-500/10 flex items-center gap-4">
                                    <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white shadow-lg">
                                        <Award size={20} />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-emerald-600 uppercase tracking-widest leading-none mb-1">Pedigrí Verificado</p>
                                        <p className="text-xs font-black text-slate-800 font-mono tracking-tighter">{pet.pedigreeReg}</p>
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => {
                                        setSelectedAdoptionPet(pet);
                                        setShowAdoptionModal(true);
                                    }}
                                    className="w-full bg-rose-600 text-white p-4 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-rose-500/20 hover:scale-105 transition-all flex items-center justify-center gap-2"
                                >
                                    <UserPlusIcon size={16} /> Vincular / Adoptar
                                </button>
                            )}
                            <div className="bg-indigo-600 p-6 rounded-[2rem] shadow-xl shadow-indigo-600/20 border-b-4 border-indigo-800 relative overflow-hidden group">
                                <div className="absolute -right-4 -bottom-4 opacity-10 group-hover:scale-110 transition-transform">
                                    <Award size={100} className="text-white" />
                                </div>
                                <p className="text-[10px] font-black text-indigo-200 uppercase tracking-[0.2em] mb-1 relative z-10">Raza del Ejemplar</p>
                                <p className="text-2xl font-black text-white italic uppercase tracking-tighter relative z-10 leading-tight">
                                    {pet.breed}
                                </p>
                                <div className="mt-4 flex gap-2 relative z-10">
                                    <span className="bg-white/20 text-white px-3 py-1 rounded-lg text-[8px] font-black uppercase tracking-widest backdrop-blur-md">Rescate Especial</span>
                                </div>
                            </div>
                        </div>
                        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Chip / Registro</p>
                            <p className="text-xs font-bold text-slate-700">{pet.microchip || pet.chip_number || 'No registrado'}</p>
                        </div>
                    </div>

                    {/* MAIN CONTENT Area */}
                    <div className="flex-1 h-full flex flex-col p-10 overflow-hidden">
                        {/* NAVIGATION TABS */}
                        <nav className="flex gap-10 border-b border-slate-200/60 mb-10 overflow-x-auto pb-4 no-scrollbar">
                            {[
                                { id: 'protocolo', label: 'Protocolo', icon: CheckCircle },
                                { id: 'crecimiento', label: 'Crecimiento', icon: Activity },
                                { id: 'carnet', label: 'Carnet', icon: ShieldCheck },
                                { id: 'historial', label: 'Historial', icon: Clock },
                                { id: 'galeria', label: 'Galería', icon: Camera },
                                { id: 'documentos', label: 'Documentos', icon: FileText }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActivePetTab(tab.id as any)}
                                    className={`flex items-center gap-3 pb-6 whitespace-nowrap font-black uppercase text-[10px] tracking-widest transition-all relative ${activePetTab === tab.id ? 'text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    <tab.icon size={16} className={activePetTab === tab.id ? 'text-orange-500' : 'text-slate-300'} />
                                    {tab.label}
                                    {activePetTab === tab.id && <div className="absolute bottom-0 left-0 w-full h-1.5 bg-[#FF6B00] rounded-full shadow-[0_4px_12px_rgba(255,107,0,0.3)]"></div>}
                                </button>
                            ))}
                        </nav>

                        {/* CONTENT Area */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                            {activePetTab === 'protocolo' && renderProtocolContent(pet)}
                            {activePetTab === 'crecimiento' && (
                                <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                                    <div className="flex items-center gap-4 mb-8">
                                        <div className="p-3 bg-emerald-100 text-emerald-600 rounded-2xl"><Activity size={24} /></div>
                                        <div>
                                            <h4 className="text-xl font-black text-slate-800 italic uppercase">Curva de Crecimiento</h4>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Seguimiento obligatorio de peso y talla</p>
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        {/* Evolution List */}
                                        <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100">
                                            <div className="grid grid-cols-4 text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4 px-4">
                                                <span>Fecha</span>
                                                <span>Peso (Kg)</span>
                                                <span>Talla (cm)</span>
                                                <span>Estado</span>
                                            </div>
                                            <div className="space-y-2">
                                                {selectedPetHealth.growth.map(log => (
                                                    <div key={log.id} className="grid grid-cols-4 bg-white p-4 rounded-2xl shadow-sm items-center text-xs font-bold text-slate-700">
                                                        <span className="text-slate-400">{log.date}</span>
                                                        <span className="text-emerald-600">{log.weight} Kg</span>
                                                        <span className="text-blue-600">{log.height} cm</span>
                                                        <span className="text-[9px] bg-slate-100 px-2 py-1 rounded-lg text-center text-slate-500 uppercase font-bold">{log.note || 'Control'}</span>
                                                    </div>
                                                ))}
                                                {selectedPetHealth.growth.length === 0 && (
                                                    <p className="text-center text-xs text-slate-400 py-4 italic uppercase font-bold tracking-widest">No hay registros aún</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Add New Log */}
                                        <div className="bg-indigo-50 rounded-3xl p-6 border border-indigo-100">
                                            <h5 className="text-[10px] font-black text-[#3F51B5] uppercase tracking-widest mb-4">Actualizar Registro Semanal</h5>
                                            <div className="grid grid-cols-2 gap-4">
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    placeholder="Nuevo Peso (Kg)"
                                                    className="bg-white p-3 rounded-xl border border-indigo-200 outline-none focus:ring-2 focus:ring-indigo-300 transition-all font-bold text-xs"
                                                    value={growthForm.weight}
                                                    onChange={(e) => setGrowthForm({ ...growthForm, weight: e.target.value.replace(/[^0-9.]/g, '') })}
                                                />
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    placeholder="Nueva Talla (cm)"
                                                    className="bg-white p-3 rounded-xl border border-indigo-200 outline-none focus:ring-2 focus:ring-indigo-300 transition-all font-bold text-xs"
                                                    value={growthForm.height}
                                                    onChange={(e) => setGrowthForm({ ...growthForm, height: e.target.value.replace(/[^0-9.]/g, '') })}
                                                />
                                                <button
                                                    onClick={handleSaveGrowth}
                                                    disabled={actionLoading || !growthForm.weight}
                                                    className="col-span-2 bg-[#3F51B5] text-white py-3 rounded-xl font-black uppercase text-[10px] shadow-lg hover:bg-[#303F9F] transition-all hover:scale-[1.02] disabled:opacity-50"
                                                >
                                                    {actionLoading ? 'Actualizando...' : 'Guardar Evolución'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {activePetTab === 'carnet' && (
                                !selectedPetDetail?.birthDate ? (
                                    <div className="bg-orange-50 p-10 rounded-3xl border border-orange-200 text-center animate-in fade-in zoom-in duration-300">
                                        <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Activity size={32} />
                                        </div>
                                        <h4 className="text-xl font-black text-orange-600 uppercase italic mb-2">Edad Estimada Requerida</h4>
                                        <p className="text-xs font-bold text-orange-500 mb-6 uppercase tracking-widest">Para generar el calendario de vacunas y desparasitaciones, ingresa una edad aproximada.</p>
                                        <div className="flex justify-center items-center gap-4 max-w-sm mx-auto">
                                            <input type="number" id="estYears" placeholder="Años (ej. 2)" className="flex-1 p-4 rounded-2xl border-2 border-orange-200 outline-none font-bold text-slate-700 text-center focus:border-orange-500" min="0" />
                                            <input type="number" id="estMonths" placeholder="Meses (ej. 6)" className="flex-1 p-4 rounded-2xl border-2 border-orange-200 outline-none font-bold text-slate-700 text-center focus:border-orange-500" min="0" max="11" />
                                        </div>
                                        <button onClick={async () => {
                                            const y = parseInt((document.getElementById('estYears') as HTMLInputElement).value) || 0;
                                            const m = parseInt((document.getElementById('estMonths') as HTMLInputElement).value) || 0;
                                            if (y === 0 && m === 0) return alert("Ingresa al menos 1 mes u 1 año de edad aproximada.");

                                            const bd = new Date();
                                            bd.setMonth(bd.getMonth() - m);
                                            bd.setFullYear(bd.getFullYear() - y);
                                            const newDate = bd.toISOString().split('T')[0];

                                            try {
                                                setActionLoading(true);
                                                await api.updatePet(selectedPetDetail.id, { birthDate: newDate });
                                                const updatedPet = { ...selectedPetDetail, birthDate: newDate };
                                                setSelectedPetDetail(updatedPet);

                                                // Sync with main list
                                                const newResidents = residents.map(r => r.id === updatedPet.id ? updatedPet : r);
                                                setResidents(newResidents);

                                                setSuccessModal({ isOpen: true, title: 'Edad Registrada', message: `Fecha aproximada guardada: ${newDate}` });
                                            } catch (e) {
                                                alert("Error al guardar la edad");
                                            } finally {
                                                setActionLoading(false);
                                            }
                                        }} className="mt-6 bg-orange-500 hover:bg-orange-600 active:scale-95 transition-all text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-orange-500/20">
                                            {actionLoading ? 'Guardando...' : 'Generar Carnet Base'}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                                        <div className="flex justify-between items-center mb-6">
                                            <h4 className="text-xl font-black text-slate-800 italic uppercase">Libreta de Salud</h4>
                                            <p className="text-[10px] font-black uppercase text-rose-500 bg-rose-50 px-4 py-2 rounded-xl">Fecha Nacimiento Est: {selectedPetDetail.birthDate}</p>
                                        </div>
                                        <BreederPetHealth
                                            pet={selectedPetDetail}
                                            type="vaccines"
                                            onAddRecord={() => {
                                                setHealthFormData({ date: new Date().toISOString().split('T')[0] });
                                                setShowVaccineModal(true);
                                            }}
                                        />
                                    </div>
                                )
                            )}

                            {activePetTab === 'historial' && (
                                <BreederPetHealth
                                    pet={selectedPetDetail}
                                    type="consultations"
                                    onAddRecord={() => {
                                        setHealthFormData({ date: new Date().toISOString().split('T')[0] });
                                        setShowConsultationModal(true);
                                    }}
                                />
                            )}

                            {activePetTab === 'documentos' && (
                                <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                                    <div className="flex justify-between items-center mb-8">
                                        <div>
                                            <h4 className="text-xl font-black text-slate-800 italic uppercase">Expediente Digital</h4>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Documentación fundamental del ejemplar</p>
                                        </div>
                                        <button
                                            onClick={() => setShowAddDocModal(true)}
                                            className="bg-slate-900 text-white px-6 py-3 rounded-2xl font-black uppercase text-[10px] flex items-center gap-2 hover:scale-105 transition-transform shadow-lg"
                                        >
                                            <Plus size={16} /> Agregar
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        {petDocs.map((doc, i) => (
                                            <div key={i} className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm flex items-start gap-4">
                                                <div className="w-12 h-12 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center shrink-0">
                                                    <FileText size={20} />
                                                </div>
                                                <div className="flex-1">
                                                    <h5 className="font-black text-slate-800 uppercase text-xs mb-1">{doc.title}</h5>
                                                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">{doc.document_type}</p>
                                                    <a href={doc.file_url} target="_blank" rel="noreferrer" className="text-[10px] font-black text-[#3F51B5] uppercase tracking-widest flex items-center gap-2 hover:underline">
                                                        Descargar <ExternalLink size={12} />
                                                    </a>
                                                </div>
                                            </div>
                                        ))}
                                        {petDocs.length === 0 && (
                                            <div className="col-span-full py-10 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] text-center">
                                                <Files size={32} className="mx-auto text-slate-300 mb-2" />
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Sin documentos anexos</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activePetTab === 'galeria' && (
                                <div className="space-y-6 animate-in slide-in-from-right-4 duration-500">
                                    <div className="flex justify-between items-center mb-8">
                                        <div>
                                            <h4 className="text-xl font-black text-slate-800 italic uppercase">Galería de Imágenes</h4>
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Máximo 10 fotografías por ejemplar</p>
                                        </div>
                                        <div className="relative overflow-hidden group">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleUploadGalleryImage}
                                                disabled={actionLoading || (selectedPetHealth.gallery || []).length >= 10}
                                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed z-10"
                                            />
                                            <button
                                                disabled={actionLoading || (selectedPetHealth.gallery || []).length >= 10}
                                                className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-black uppercase text-[10px] flex items-center gap-2 hover:bg-indigo-700 transition-colors shadow-lg disabled:opacity-50"
                                            >
                                                {actionLoading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                                                {actionLoading ? 'Subiendo...' : 'Agregar Foto'}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {(selectedPetHealth.gallery || []).map((img, i) => (
                                            <div key={img.id || i} className="aspect-square relative group rounded-2xl overflow-hidden shadow-sm border border-slate-100">
                                                <img src={img.url} alt={`Gallery ${i}`} className="w-full h-full object-cover" />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                    <button onClick={() => handleDeleteGalleryImage(img.id)} disabled={actionLoading} className="p-3 bg-rose-500 text-white rounded-full hover:scale-110 transition-transform shadow-xl">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {(selectedPetHealth.gallery || []).length === 0 && (
                                        <div className="py-10 bg-slate-50 border-2 border-dashed border-slate-200 rounded-[2rem] text-center">
                                            <Camera size={32} className="mx-auto text-slate-300 mb-2" />
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">No hay imágenes en la galería</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderAdoptions = () => (
        <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-500">
            {adoptions.length === 0 ? (
                <div className="bg-white p-20 rounded-[3rem] text-center border border-slate-100 shadow-xl">
                    <p className="font-black uppercase italic text-slate-400 tracking-widest text-xl">Sin solicitudes pendientes</p>
                </div>
            ) : (
                adoptions.map(req => (
                    <div key={req.id} className="bg-white p-10 rounded-[2.5rem] shadow-xl border border-slate-100 flex flex-col md:flex-row gap-8 items-center group hover:border-[#FF6B00] transition-all">
                        <div className="w-24 h-24 rounded-3xl overflow-hidden shadow-lg shrink-0">
                            <img src={req.pet_image || 'https://images.unsplash.com/photo-1543466835-00a7907e9de1'} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 text-center md:text-left">
                            <h4 className="text-xl font-black text-slate-800 uppercase italic">Solicitud para {req.pet_name}</h4>
                            <p className="text-[10px] font-black text-[#FF6B00] uppercase tracking-widest mt-1">Adoptante: {req.applicant_name}</p>
                            <div className="flex flex-wrap gap-4 mt-4 justify-center md:justify-start">
                                <span className="flex items-center gap-2 text-[10px] font-bold text-slate-500 bg-slate-50 px-3 py-1 rounded-full"><Mail size={12} /> {req.applicant_email}</span>
                                <span className="flex items-center gap-2 text-[10px] font-bold text-slate-500 bg-slate-50 px-3 py-1 rounded-full"><Phone size={12} /> {req.applicant_phone}</span>
                                <span className="flex items-center gap-2 text-[10px] font-bold text-slate-500 bg-slate-50 px-3 py-1 rounded-full"><Home size={12} /> {req.home_type}</span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {req.status === 'pending' && (
                                <>
                                    <button onClick={() => handleUpdateAdoptionStatus(req.id, 'approved')} className="p-4 bg-emerald-500 text-white rounded-2xl hover:scale-105 transition-all shadow-lg shadow-emerald-500/20"><CheckCircle size={24} /></button>
                                    <button onClick={() => handleUpdateAdoptionStatus(req.id, 'rejected')} className="p-4 bg-rose-500 text-white rounded-2xl hover:scale-105 transition-all shadow-lg shadow-rose-500/20"><X size={24} /></button>
                                </>
                            )}
                            <span className={`px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest ${req.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : req.status === 'rejected' ? 'bg-rose-100 text-rose-600' : 'bg-amber-100 text-amber-600'}`}>
                                {req.status === 'pending' ? 'Pendiente' : req.status === 'approved' ? 'Aprobado' : 'Rechazado'}
                            </span>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    // Padrinos: módulo completo en ShelterSponsors.tsx (backend_php/sponsors.php). La lista antigua leía
    // una tabla que no existía y se caía apenas hubiera un padrino (usaba spon.user_name, que no venía).
    const renderSponsors = () => <ShelterSponsors isDemo={isDemoMode} />;

    const renderSettings = () => (
        <div className="max-w-5xl mx-auto space-y-8 animate-in zoom-in-95 duration-500">
            {/* Main Info Card */}
            <div className="bg-white rounded-[3rem] p-12 shadow-2xl border border-slate-100">
                <div className="flex items-center gap-6 mb-12">
                    <div className="w-20 h-20 bg-orange-50 text-orange-500 rounded-[1.5rem] flex items-center justify-center rotate-6 shadow-xl">
                        <Settings size={40} />
                    </div>
                    <div>
                        <h3 className="text-3xl font-black text-slate-800 uppercase italic tracking-tighter leading-none">Perfil del Albergue</h3>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-2">Personaliza tu identidad y ubicación</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                    <div className="space-y-6">
                        <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest bg-slate-100 px-4 py-1.5 rounded-full w-fit">Información General</p>
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-3">Nombre Público</label>
                            <input value={shelterProfile.name} onChange={e => setShelterProfile({ ...shelterProfile, name: e.target.value })} className="w-full bg-slate-50 border border-slate-100 px-6 py-4 rounded-2xl font-bold text-slate-800 outline-none focus:ring-4 focus:ring-orange-500/5 focus:border-orange-500 transition-all" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-3">Fono Contacto</label>
                                <input value={shelterProfile.phone} onChange={e => setShelterProfile({ ...shelterProfile, phone: e.target.value.replace(/\D/g, '') })} className="w-full bg-slate-50 border border-slate-100 px-6 py-4 rounded-2xl font-bold text-slate-800" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-3">Capacidad</label>
                                <input type="text" inputMode="numeric" value={shelterProfile.capacity} onChange={e => setShelterProfile({ ...shelterProfile, capacity: e.target.value.replace(/\D/g, '') })} className="w-full bg-slate-50 border border-slate-100 px-6 py-4 rounded-2xl font-bold text-slate-800" />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest bg-indigo-50 text-indigo-600 px-4 py-1.5 rounded-full w-fit">Encargado Responsable</p>
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-3">Nombre Completo</label>
                            <div className="relative">
                                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                                <input value={shelterProfile.manager_name} onChange={e => setShelterProfile({ ...shelterProfile, manager_name: e.target.value })} className="w-full bg-slate-50 border border-slate-100 pl-12 pr-6 py-4 rounded-2xl font-bold text-slate-800" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-3">{(LOCATIONS[shelterProfile.country] || LOCATIONS['Chile']).idName}</label>
                                <input
                                    value={shelterProfile.manager_rut}
                                    placeholder={(LOCATIONS[shelterProfile.country] || LOCATIONS['Chile']).idPlaceholder}
                                    onChange={e => setShelterProfile({ ...shelterProfile, manager_rut: formatGenericID(e.target.value, shelterProfile.country) })}
                                    className="w-full bg-slate-50 border border-slate-100 px-6 py-4 rounded-2xl font-bold text-slate-800"
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-3">Fono Directo</label>
                                <input value={shelterProfile.manager_phone} onChange={e => setShelterProfile({ ...shelterProfile, manager_phone: e.target.value.replace(/\D/g, '') })} className="w-full bg-slate-50 border border-slate-100 px-6 py-4 rounded-2xl font-bold text-slate-800" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Location Picker */}
                <div className="space-y-8 border-t border-slate-100 pt-12">
                    <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest bg-emerald-50 text-emerald-600 px-4 py-1.5 rounded-full w-fit">Ubicación Geográfica</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-3">País</label>
                            <select value={shelterProfile.country} onChange={e => setShelterProfile({ ...shelterProfile, country: e.target.value, region: '', comuna: '' })} className="w-full bg-slate-50 border border-slate-100 px-6 py-4 rounded-2xl font-bold appearance-none">
                                {countries.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-3">Región</label>
                            <select value={shelterProfile.region} onChange={e => setShelterProfile({ ...shelterProfile, region: e.target.value, comuna: '' })} className="w-full bg-slate-50 border border-slate-100 px-6 py-4 rounded-2xl font-bold appearance-none">
                                <option value="">Seleccionar...</option>
                                {regions.map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-3">Comuna / Ciudad</label>
                            <select value={shelterProfile.comuna} onChange={e => setShelterProfile({ ...shelterProfile, comuna: e.target.value })} className="w-full bg-slate-50 border border-slate-100 px-6 py-4 rounded-2xl font-bold appearance-none">
                                <option value="">Seleccionar...</option>
                                {communes.map((c: any) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="h-96 rounded-[2.5rem] overflow-hidden border-4 border-white shadow-2xl relative">
                        <MapContainer center={[shelterProfile.lat, shelterProfile.lng]} zoom={13} style={{ height: '100%', width: '100%' }}>
                            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}" attribution='Tiles &copy; Esri | &copy; OpenStreetMap contributors' />
                            <LocationMarker position={[shelterProfile.lat, shelterProfile.lng]} setPosition={(pos) => setShelterProfile({ ...shelterProfile, lat: pos[0], lng: pos[1] })} />
                            <ChangeMapCenter center={[shelterProfile.lat, shelterProfile.lng]} />
                        </MapContainer>
                        <div className="absolute top-4 right-4 z-[1000] p-4 bg-white/90 backdrop-blur-md rounded-2xl shadow-xl border border-slate-200 max-w-[200px]">
                            <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Coordenadas</p>
                            <p className="text-[9px] font-bold text-slate-700 truncate">{shelterProfile.lat.toFixed(6)}, {shelterProfile.lng.toFixed(6)}</p>
                            <p className="text-[8px] mt-2 text-indigo-500 font-bold leading-tight italic">Haz clic en el mapa para posicionar el albergue con precisión.</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-3">Dirección Exacta</label>
                        <input value={shelterProfile.address} onChange={e => setShelterProfile({ ...shelterProfile, address: e.target.value })} placeholder="Ej: Av. Las Encinas 450, Piso 2" className="w-full bg-slate-50 border border-slate-100 px-6 py-4 rounded-2xl font-bold text-slate-800" />
                    </div>
                </div>

                {/* Protocol Configuration Section */}
                <div className="mt-16 pt-16 border-t border-slate-100">
                    <div className="flex items-center gap-6 mb-12">
                        <div className="w-20 h-20 bg-emerald-50 text-emerald-500 rounded-[1.5rem] flex items-center justify-center -rotate-6 shadow-xl">
                            <CheckCircle size={40} />
                        </div>
                        <div>
                            <h3 className="text-3xl font-black text-slate-800 uppercase italic tracking-tighter leading-none">Protocolo de Rescate</h3>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-2">Personaliza el checklist de rehabilitación</p>
                        </div>
                    </div>

                    <div className="bg-slate-50 rounded-[2.5rem] p-10 border border-slate-100">
                        <div className="flex justify-between items-center mb-10">
                            <div>
                                <h5 className="font-black text-slate-800 uppercase italic tracking-widest text-sm">Tareas del Protocolo</h5>
                                <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Estas tareas aparecerán en el perfil de cada mascota residente</p>
                            </div>
                            <button
                                onClick={() => setProtocolOptions([...protocolOptions, { id: Date.now().toString(), label: 'Nueva Tarea', manual: true }])}
                                className="bg-indigo-600 text-white px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-indigo-600/20 hover:scale-105 transition-all flex items-center gap-2"
                            >
                                <Plus size={16} /> Agregar Tarea
                            </button>
                        </div>

                        <div className="space-y-4">
                            {protocolOptions.map((opt, index) => (
                                <div key={opt.id} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex items-center gap-6 group">
                                    <div className="w-10 h-10 bg-slate-100 text-slate-400 rounded-xl flex items-center justify-center font-black text-xs">{index + 1}</div>
                                    <input
                                        value={opt.label}
                                        onChange={(e) => {
                                            const newOpts = [...protocolOptions];
                                            newOpts[index].label = e.target.value;
                                            setProtocolOptions(newOpts);
                                        }}
                                        className="flex-1 bg-transparent font-bold text-slate-700 outline-none placeholder:text-slate-300"
                                        placeholder="Ej: Desparasitación interna concluida..."
                                    />
                                    <button
                                        onClick={() => setProtocolOptions(protocolOptions.filter(o => o.id !== opt.id))}
                                        className="p-3 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-12 flex justify-end">
                    <button onClick={handleUpdateProfile} disabled={actionLoading} className="px-12 py-6 bg-slate-900 text-white rounded-[2rem] font-black uppercase text-xs tracking-widest shadow-2xl hover:bg-black transition-all flex items-center gap-3 active:scale-95 disabled:opacity-50">
                        {actionLoading ? <Loader2 className="animate-spin" /> : <Save size={20} />} Guardar Cambios del Sistema
                    </button>
                </div>
            </div>
        </div>
    );

    if (loading && !residents.length) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center">
                    <Loader2 className="animate-spin text-orange-500 mx-auto mb-4" size={48} />
                    <p className="font-black uppercase italic text-slate-400 tracking-widest">Sincronizando ShelterPro...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F8F9FA] flex flex-col lg:flex-row relative">
            {/* Edge Trigger Sensor */}
            {!isSidebarOpen && (
                <div
                    className="fixed inset-y-0 left-0 w-4 z-[60] cursor-e-resize"
                    onMouseEnter={() => setIsSidebarOpen(true)}
                />
            )}

            {/* Sidebar Navigation */}
            <aside className={`max-w-xs hidden lg:flex bg-slate-900 lg:h-screen p-8 flex-col gap-8 lg:fixed lg:inset-y-0 lg:left-0 z-50 transition-all duration-500 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}>
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-[#FF6B00] rounded-2xl rotate-6 shadow-xl shadow-orange-600/20">
                        <Home className="text-white" size={28} />
                    </div>
                    <div>
                        <h1 className="text-white text-2xl font-black uppercase italic tracking-tighter leading-none">Shelter<span className="text-[#FF6B00]">Pro</span></h1>
                        <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em] mt-1">Ecosistema Rescate</p>
                    </div>
                </div>

                <nav className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 scrollbar-none mt-4">
                    {[
                        { id: 'dashboard', label: 'Panel Control', icon: Activity },
                        { id: 'residentes', label: 'Residentes', icon: Users },
                        ...(selectedPetDetail ? [{ id: 'pet_detail', label: 'Mascota', icon: Dog }] : []),
                        { id: 'adopciones', label: 'Adopciones', icon: Heart },
                        { id: 'padrinos', label: 'Padrinos', icon: Award },
                        { id: 'settings', label: 'Configuración', icon: Settings }
                    ].map((item) => (
                        <button
                            key={item.id}
                            onClick={() => {
                                setActiveTab(item.id as any);
                                if (item.id !== 'pet_detail') setSelectedPetDetail(null);
                                setIsSidebarOpen(false);
                            }}
                            className={`flex items-center gap-5 px-6 py-4 rounded-[1.5rem] font-black uppercase text-[11px] tracking-widest transition-all shrink-0 ${activeTab === item.id
                                ? 'bg-[#FF6B00] text-white shadow-2xl shadow-orange-600/30 -translate-y-1 lg:translate-y-0 lg:translate-x-2'
                                : 'text-slate-500 hover:text-white hover:bg-white/5'
                                }`}
                        >
                            <item.icon size={20} />
                            <span>{item.label}</span>
                        </button>
                    ))}

                    <div className="h-px bg-white/5 my-4 hidden lg:block"></div>

                    <button
                        onClick={() => {
                            const url = `${window.location.origin}/p/${user?.id}`;
                            window.open(url, '_blank');
                        }}
                        className="flex lg:w-full items-center gap-5 px-6 py-4 rounded-[1.5rem] font-black uppercase text-[11px] tracking-widest transition-all shrink-0 text-emerald-400 hover:bg-emerald-500/10 border border-dashed border-emerald-500/30 mb-2"
                    >
                        <ExternalLink size={20} />
                        <span>Página Pública</span>
                    </button>
                    <button
                        onClick={() => onExit(View.USER_HUB)}
                        className="flex lg:w-full items-center gap-5 px-6 py-4 rounded-[1.5rem] font-black uppercase text-[11px] tracking-widest transition-all shrink-0 text-rose-500 hover:bg-rose-500/10 border border-dashed border-rose-500/30"
                    >
                        <X size={20} />
                        <span>Cerrar Panel</span>
                    </button>
                </nav>

                <div className="mt-auto hidden lg:block bg-white/5 p-6 rounded-[2.5rem] border border-white/5">
                    <div className="flex items-center gap-4 mb-5">
                        <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-orange-500/50 p-0.5">
                            <img src={user?.imageUrl} className="w-full h-full object-cover rounded-xl" />
                        </div>
                        <div className="overflow-hidden">
                            <p className="text-xs font-black text-white uppercase italic truncate">{user?.name}</p>
                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Manager Albergue</p>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content Areas */}
            <main className={`flex-1 p-4 pb-32 lg:p-12 lg:pb-12 overflow-y-auto custom-scrollbar relative transition-all duration-500 ease-in-out ${isSidebarOpen ? 'lg:ml-80' : 'lg:ml-0'}`}>

                {activeTab === 'pet_detail' ? renderPetDetail() : (
                    <>
                        {activeTab === 'dashboard' && renderDashboard()}
                        {activeTab === 'residentes' && renderResidents()}
                        {activeTab === 'adopciones' && renderAdoptions()}
                        {activeTab === 'padrinos' && renderSponsors()}
                        {activeTab === 'settings' && renderSettings()}
                    </>
                )}
            </main>

            {/* Nuevo Rescate Modal */}
            {showRegisterModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/90 backdrop-blur-xl p-4 overflow-y-auto">
                    <div className="bg-white rounded-[3.5rem] p-0 max-w-2xl w-full shadow-2xl my-8 animate-in zoom-in-95 duration-300 overflow-hidden relative border border-slate-100">
                        <button
                            onClick={() => setShowRegisterModal(false)}
                            className="absolute top-6 right-6 z-50 p-3 bg-white/20 backdrop-blur-md text-white rounded-full hover:bg-white/40 shadow-lg transition-all"
                        >
                            <X size={24} />
                        </button>

                        <div className="max-h-[85vh] overflow-y-auto">
                            <AddPetForm
                                userId={user?.id || ''}
                                shelterId={shelterProfile.id}
                                rescueStatus="shelter"
                                customTitle="MASCOTA RESCATADA"
                                hideContainer={true}
                                onPetAdded={() => {
                                    setShowRegisterModal(false);
                                    fetchAllData();
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {successModal.isOpen && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={() => setSuccessModal({ ...successModal, isOpen: false })}>
                    <div className="bg-white rounded-[3rem] p-10 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-500 text-center relative overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
                        <div className="w-24 h-24 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl animate-bounce-slow">
                            <CheckCircle size={48} />
                        </div>
                        <h3 className="text-2xl font-black text-slate-800 uppercase italic mb-2 leading-tight">{successModal.title}</h3>
                        <p className="text-xs font-bold text-slate-500 leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: successModal.message }}></p>
                        <button
                            onClick={() => setSuccessModal({ ...successModal, isOpen: false })}
                            className="w-full bg-slate-900 text-white py-4 rounded-2xl font-black uppercase text-xs shadow-lg hover:bg-black transition-all hover:scale-105"
                        >
                            ¡Entendido!
                        </button>
                    </div>
                </div>
            )}

            {/* Vaccine Record Modal */}
            {showVaccineModal && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-sky-950/90 backdrop-blur-xl p-4">
                    <div className="bg-white rounded-[3.5rem] p-10 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 bg-sky-100 text-sky-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 rotate-6 shadow-xl">
                                <Syringe size={40} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-800 uppercase italic">Registrar Vacuna</h3>
                            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-2">Ingresa los datos para {selectedPetDetail?.name}</p>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nombre de Vacuna / Desparasitante</label>
                                <input
                                    className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-2xl outline-none focus:border-sky-500 font-bold text-sky-600"
                                    placeholder="EJ: Sextuple (Refuerzo)"
                                    value={healthFormData.name || ''}
                                    onChange={e => setHealthFormData({ ...healthFormData, name: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Fecha de Aplicación</label>
                                <input
                                    type="date"
                                    className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-2xl outline-none focus:border-sky-500 font-bold"
                                    value={healthFormData.date || ''}
                                    onChange={e => setHealthFormData({ ...healthFormData, date: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Clínica / Lugar</label>
                                <input
                                    className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-2xl outline-none focus:border-sky-500 font-bold"
                                    placeholder="EJ: Veterinaria San Roque"
                                    value={healthFormData.place || ''}
                                    onChange={e => setHealthFormData({ ...healthFormData, place: e.target.value })}
                                />
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setShowVaccineModal(false)}
                                    className="flex-1 bg-slate-100 text-slate-500 py-4 rounded-2xl font-black uppercase text-xs"
                                >
                                    Cerrar
                                </button>
                                <button
                                    onClick={handleAddVaccine}
                                    className="flex-1 bg-sky-600 text-white py-4 rounded-2xl font-black uppercase text-xs shadow-lg"
                                >
                                    Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Pet Document Modal */}
            {showAddDocModal && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-rose-950/90 backdrop-blur-xl p-4">
                    <div className="bg-white rounded-[3.5rem] p-10 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300" onClick={e => e.stopPropagation()}>
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 bg-rose-100 text-rose-600 rounded-[2rem] flex items-center justify-center mx-auto mb-6 rotate-6 shadow-xl">
                                <Files size={40} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-800 uppercase italic">Subir Documento</h3>
                            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-2">Expediente oficial de {selectedPetDetail?.name}</p>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Título del Documento</label>
                                <input
                                    className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-2xl outline-none focus:border-rose-500 font-bold"
                                    placeholder="EJ: Certificado de Displasia"
                                    value={docFormData.title}
                                    onChange={e => setDocFormData({ ...docFormData, title: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Categoría</label>
                                <select
                                    className="w-full bg-slate-50 border-2 border-slate-100 p-4 rounded-2xl outline-none focus:border-rose-500 font-bold"
                                    value={docFormData.type}
                                    onChange={e => setDocFormData({ ...docFormData, type: e.target.value })}
                                >
                                    <option value="Pedigree">Pedigrí / Certificado Origen</option>
                                    <option value="Examen de Salud">Examen de Salud</option>
                                    <option value="ADN">Perfil de ADN</option>
                                    <option value="Microchip">Certificado Microchip</option>
                                    <option value="Contrato">Contrato / Otros</option>
                                </select>
                            </div>

                            <div
                                className="p-8 border-2 border-dashed border-slate-200 rounded-[2.5rem] text-center hover:border-rose-500 transition-colors cursor-pointer group relative bg-slate-50"
                                onClick={() => document.getElementById('pet-doc-upload')?.click()}
                            >
                                <input
                                    id="pet-doc-upload"
                                    type="file"
                                    accept="image/*,application/pdf"
                                    className="hidden"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            setDocFormData({ ...docFormData, file: e.target.files[0] });
                                        }
                                    }}
                                />
                                {docFormData.file ? (
                                    <>
                                        <CheckCircle size={32} className="text-emerald-500 mx-auto mb-3" />
                                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">{docFormData.file.name}</p>
                                    </>
                                ) : (
                                    <>
                                        <Upload size={32} className="text-slate-300 mx-auto mb-3 group-hover:text-rose-500 transition-colors" />
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-tight">Seleccionar archivo PDF o Imagen</p>
                                    </>
                                )}
                            </div>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => setShowAddDocModal(false)}
                                    className="flex-1 bg-slate-100 text-slate-500 py-4 rounded-2xl font-black uppercase text-xs"
                                >
                                    Cerrar
                                </button>
                                <button
                                    disabled={actionLoading || !docFormData.file || !docFormData.title}
                                    onClick={handleUploadDocument}
                                    className="flex-1 bg-rose-600 text-white py-4 rounded-2xl font-black uppercase text-xs shadow-lg disabled:opacity-50"
                                >
                                    {actionLoading ? 'Subiendo...' : 'Guardar Documento'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Consultation Record Modal */}
            {showConsultationModal && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-violet-950/90 backdrop-blur-xl p-4">
                    <div className="bg-white rounded-[3rem] w-full max-w-lg shadow-2xl animate-in zoom-in-95 duration-300 overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                        <div className="bg-violet-600 p-8 text-center relative overflow-hidden shrink-0">
                            <div className="absolute -left-2 -bottom-2 text-white/10 rotate-12"><Stethoscope size={80} /></div>
                            <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter relative z-10">Registrar Atención</h3>
                            <p className="text-white/80 text-[10px] font-bold uppercase tracking-widest mt-1 relative z-10">Nuevo registro médico para {selectedPetDetail?.name}</p>
                            <button onClick={() => setShowConsultationModal(false)} className="absolute top-6 right-6 text-white/80 hover:text-white p-2">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-8 space-y-6 overflow-y-auto flex-1">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Tipo</label>
                                    <select
                                        className={`w-full bg-slate-50 border-2 rounded-2xl px-4 py-3 font-bold text-slate-700 outline-none transition-all ${consultationErrors.type ? 'border-rose-500 bg-rose-50' : 'border-slate-100 focus:border-violet-500'}`}
                                        value={healthFormData.type || 'Control'}
                                        onChange={(e) => {
                                            setHealthFormData({ ...healthFormData, type: e.target.value });
                                            if (consultationErrors.type) setConsultationErrors({ ...consultationErrors, type: false });
                                        }}
                                    >
                                        <option value="Control">Control</option>
                                        <option value="Urgencia">Urgencia</option>
                                        <option value="Vacunación">Vacunación</option>
                                        <option value="Cirugía">Cirugía</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Fecha</label>
                                    <input
                                        type="date"
                                        className={`w-full bg-slate-50 border-2 rounded-2xl px-4 py-3 font-bold text-slate-700 outline-none transition-all ${consultationErrors.date ? 'border-rose-500 bg-rose-50' : 'border-slate-100 focus:border-violet-500'}`}
                                        value={healthFormData.date || ''}
                                        onChange={(e) => {
                                            setHealthFormData({ ...healthFormData, date: e.target.value });
                                            if (consultationErrors.date) setConsultationErrors({ ...consultationErrors, date: false });
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Lugar / Clínica</label>
                                <input
                                    type="text"
                                    placeholder="Veterinaria..."
                                    className={`w-full bg-slate-50 border-2 rounded-2xl px-4 py-3 font-bold text-slate-700 outline-none transition-all ${consultationErrors.place ? 'border-rose-500 bg-rose-50' : 'border-slate-100 focus:border-violet-500'}`}
                                    value={healthFormData.place || ''}
                                    onChange={(e) => {
                                        setHealthFormData({ ...healthFormData, place: e.target.value });
                                        if (consultationErrors.place) setConsultationErrors({ ...consultationErrors, place: false });
                                    }}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Médico Profesional</label>
                                <input
                                    type="text"
                                    placeholder="Nombre del veterinario..."
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-3 font-bold text-slate-700 outline-none focus:border-violet-500 transition-all"
                                    value={healthFormData.doctor || ''}
                                    onChange={(e) => setHealthFormData({ ...healthFormData, doctor: e.target.value })}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Diagnóstico</label>
                                <textarea
                                    placeholder="¿Qué tiene la mascota?"
                                    rows={2}
                                    className={`w-full bg-slate-50 border-2 rounded-2xl px-4 py-2 font-bold text-slate-700 outline-none transition-all resize-none text-xs ${consultationErrors.diagnosis ? 'border-rose-500 bg-rose-50' : 'border-slate-100 focus:border-violet-500'}`}
                                    value={healthFormData.diagnosis || ''}
                                    onChange={(e) => {
                                        setHealthFormData({ ...healthFormData, diagnosis: e.target.value });
                                        if (consultationErrors.diagnosis) setConsultationErrors({ ...consultationErrors, diagnosis: false });
                                    }}
                                ></textarea>
                            </div>

                            <div className="space-y-1">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Tratamiento</label>
                                <textarea
                                    placeholder="Recomendaciones..."
                                    rows={2}
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-2 font-bold text-slate-700 outline-none focus:border-violet-500 transition-all resize-none text-xs"
                                    value={healthFormData.recommendation || ''}
                                    onChange={(e) => setHealthFormData({ ...healthFormData, recommendation: e.target.value })}
                                ></textarea>
                            </div>

                            <div className="space-y-1 mt-2">
                                <label className="text-[10px] font-black text-violet-600 uppercase tracking-widest ml-2">Medicamentos (Opcional)</label>
                                <div className="bg-slate-50 p-4 rounded-3xl space-y-3 border border-slate-200">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="col-span-2">
                                            <input type="text" placeholder="Medicamento" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-xs font-bold outline-none" value={prescriptionForm.name} onChange={(e) => setPrescriptionForm({ ...prescriptionForm, name: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="text-[8px] font-black text-slate-400 uppercase ml-2">Cada (Hrs)</label>
                                            <input type="number" placeholder="8" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-xs font-bold outline-none" value={prescriptionForm.freq} onChange={(e) => setPrescriptionForm({ ...prescriptionForm, freq: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="text-[8px] font-black text-slate-400 uppercase ml-2">Días</label>
                                            <input type="number" placeholder="5" className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2 text-xs font-bold outline-none" value={prescriptionForm.duration} onChange={(e) => setPrescriptionForm({ ...prescriptionForm, duration: e.target.value })} />
                                        </div>
                                    </div>
                                    <button onClick={handleAddPrescription} className="w-full py-2 bg-violet-50 text-violet-600 rounded-xl text-[10px] font-black uppercase hover:bg-violet-100 transition-all flex items-center justify-center gap-2">
                                        <Plus size={14} strokeWidth={3} /> Agregar Receta
                                    </button>

                                    {tempPrescriptions.length > 0 && (
                                        <div className="space-y-2 pt-2 border-t border-slate-200">
                                            {tempPrescriptions.map((p: any) => (
                                                <div key={p.id} className="flex justify-between items-center bg-white p-3 rounded-xl border border-slate-100 shadow-sm">
                                                    <div>
                                                        <p className="text-[10px] font-black text-slate-700 uppercase">{p.name}</p>
                                                        <p className="text-[8px] text-slate-400 font-bold uppercase">C/{p.freq} hrs por {p.duration} días</p>
                                                    </div>
                                                    <button onClick={() => handleRemovePrescription(p.id)} className="text-rose-400 hover:text-rose-600 p-1">
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-4 p-8 bg-slate-50 shrink-0">
                            <button
                                onClick={() => setShowConsultationModal(false)}
                                className="flex-1 bg-white border-2 border-slate-200 text-slate-400 py-4 rounded-2xl font-black uppercase text-xs hover:bg-slate-100 transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleAddConsultation}
                                className="flex-1 bg-violet-600 text-white py-4 rounded-2xl font-black uppercase text-xs shadow-xl hover:bg-violet-700 hover:scale-105 transition-all"
                            >
                                Guardar Atención
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Adoption / User Search Modal */}
            {showAdoptionModal && (
                <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/90 backdrop-blur-xl p-4" onClick={() => setShowAdoptionModal(false)}>
                    <div className="bg-white rounded-[3rem] p-10 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300 flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
                        <div className="text-center mb-8 shrink-0">
                            <div className="w-20 h-20 bg-rose-100 text-rose-600 rounded-3xl flex items-center justify-center mx-auto mb-6 rotate-6 shadow-lg">
                                <UserPlusIcon size={40} />
                            </div>
                            <h3 className="text-2xl font-black text-slate-800 uppercase italic">Vincular Dueño</h3>
                            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-2">Busca al usuario que adoptará a {selectedAdoptionPet?.name}</p>
                        </div>

                        <div className="relative mb-6 shrink-0">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                            <input
                                type="text"
                                placeholder="Escribe nombre o email (mín 3 carac.)..."
                                className="w-full bg-slate-50 border-2 border-slate-100 pl-12 pr-4 py-4 rounded-2xl outline-none focus:border-rose-500 font-bold transition-all"
                                value={userSearchTerm}
                                onChange={(e) => handleSearchUsers(e.target.value)}
                                autoFocus
                            />
                            {isSearchingUsers && (
                                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                    <Loader2 className="animate-spin text-rose-500" size={18} />
                                </div>
                            )}
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scrollbar">
                            {foundUsers.map(u => (
                                <button
                                    key={u.id}
                                    onClick={() => handleStartAdoption(selectedAdoptionPet, u)}
                                    className="w-full p-4 rounded-2xl bg-slate-50 border border-slate-100 hover:border-rose-500 hover:bg-rose-50 transition-all flex items-center gap-4 text-left group"
                                >
                                    <div className="w-12 h-12 rounded-xl overflow-hidden shadow-sm bg-white shrink-0">
                                        <img src={u.image_url || 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba'} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-black text-slate-800 uppercase italic truncate">{u.name}</p>
                                        <p className="text-[10px] font-bold text-slate-400 truncate tracking-tight">{u.email}</p>
                                    </div>
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-rose-600 text-white p-2 rounded-lg shadow-lg">
                                        <ArrowRight size={14} />
                                    </div>
                                </button>
                            ))}

                            {userSearchTerm.length >= 3 && foundUsers.length === 0 && !isSearchingUsers && (
                                <div className="text-center py-10">
                                    <Search size={32} className="mx-auto text-slate-200 mb-3" />
                                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-tight">No encontramos usuarios con ese nombre o email</p>
                                </div>
                            )}

                            {userSearchTerm.length < 3 && (
                                <div className="text-center py-10 opacity-30">
                                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest italic">Ingresa al menos 3 caracteres...</p>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => setShowAdoptionModal(false)}
                            className="w-full mt-6 py-4 rounded-2xl border-2 border-slate-100 text-slate-500 font-black uppercase text-xs hover:bg-slate-50 transition-all shrink-0"
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {/* Mobile Bottom Navigation */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 lg:hidden flex justify-around p-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] shadow-[0_-4px_20px_rgba(0,0,0,0.05)]">
                {[
                    { id: 'dashboard', label: 'Panel', icon: Activity },
                    { id: 'residentes', label: 'Residentes', icon: Users },
                    { id: 'adopciones', label: 'Adopciones', icon: Heart },
                    { id: 'padrinos', label: 'Padrinos', icon: Award },
                    { id: 'settings', label: 'Ajustes', icon: Settings }
                ].map(item => (
                    <button
                        key={item.id}
                        onClick={() => {
                            setActiveTab(item.id as any);
                            if (item.id !== 'pet_detail') setSelectedPetDetail(null);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`flex flex-col items-center justify-center p-2 rounded-xl transition-all relative ${activeTab === item.id || (activeTab === 'pet_detail' && item.id === 'residentes') ? 'text-[#FF6B00] font-black scale-110' : 'text-slate-400 hover:text-orange-400'}`}
                    >
                        <item.icon size={20} strokeWidth={activeTab === item.id || (activeTab === 'pet_detail' && item.id === 'residentes') ? 2.5 : 2} />
                        <span className="text-[8px] uppercase mt-1 tracking-widest truncate">{item.label}</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

